import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  LinearProgress,
  Skeleton,
  useTheme,
  IconButton,
  Grid,
  Paper,
  Divider,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
} from '@mui/material';
import { ArrowBack, Refresh as RefreshIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import customFetch from '../utils/axios';
import { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/common/PageHeader';

// Mock data for testing the UI - will be replaced with real data
const mockData = [
  {
    name: 'Guitarra',
    date: 'Maio de 2023',
    users: [
      { id: '1', name: 'Samantha Bailey', profilePicture: '', count: 4 },
      { id: '2', name: 'Gill Jr', profilePicture: '', count: 4 },
      { id: '3', name: 'Ethan Ford', profilePicture: '', count: 2 },
      { id: '4', name: 'Gill', profilePicture: '', count: 1 },
    ]
  },
  {
    name: 'Bateria',
    date: 'Maio de 2023',
    users: [
      { id: '5', name: 'John Smith', profilePicture: '', count: 5 },
      { id: '6', name: 'Maria Garcia', profilePicture: '', count: 3 },
    ]
  },
  {
    name: 'Vocal',
    date: 'Maio de 2023',
    users: [
      { id: '7', name: 'Emma Wilson', profilePicture: '', count: 6 },
      { id: '8', name: 'James Brown', profilePicture: '', count: 4 },
      { id: '9', name: 'Sophia Lee', profilePicture: '', count: 2 },
    ]
  }
];

const Frequency = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [useMockData, setUseMockData] = useState(false); // For testing UI

  const fetchFrequencyData = async () => {
    try {
      setLoading(true);
      setError(null);
      setUseMockData(false);
      console.log('Fetching frequency data...');
      
      // Always try to get real data first
      const response = await customFetch.get(API_ENDPOINTS.SERVICES.BASE + '/frequency');
      console.log('Frequency data response:', response.data);
      
      if (response.data.positions && response.data.positions.length > 0) {
        console.log('Setting real frequency data:', response.data.positions);
        setPositions(response.data.positions);
        setLoading(false);
      } else {
        // If no real data, use mock data for demo purposes
        console.log('No data returned from API, using mock data');
        setPositions(mockData);
        setUseMockData(true);
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching frequency data:', err);
      setError(err.response?.data?.message || 'Error loading frequency data');
      // Use mock data if API fails
      console.log('API error, falling back to mock data');
      setPositions(mockData);
      setUseMockData(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFrequencyData();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    setUseMockData(false); // Try to get real data again
    await fetchFrequencyData();
    setRefreshing(false);
  };

  const handleBack = () => {
    setSelectedPosition(null);
  };

  const handleSelectPosition = (position) => {
    setSelectedPosition(position);
  };

  // Find the maximum count for each position to calculate relative percentages
  const getMaxCount = (users) => {
    if (!users || users.length === 0) return 1;
    return Math.max(...users.map(user => user.count));
  };

  // Render loading skeletons
  const renderSkeletons = () => {
    return Array(3).fill().map((_, index) => (
      <Grid item xs={12} sm={6} md={4} key={index}>
        <Card>
          <CardHeader
            title={<Skeleton variant="text" width="60%" />}
            subheader={<Skeleton variant="text" width="40%" />}
          />
          <CardContent>
            <Skeleton variant="rectangular" height={120} />
          </CardContent>
        </Card>
      </Grid>
    ));
  };

  // Render position detail view
  const renderPositionDetail = () => {
    if (!selectedPosition) return null;
    
    const maxCount = getMaxCount(selectedPosition.users);
    
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" component="h1">
            {selectedPosition.name}
          </Typography>
          <Typography variant="subtitle1" sx={{ ml: 2, color: 'text.secondary' }}>
            {selectedPosition.date || 'All time'}
          </Typography>
        </Box>
        
        <Paper elevation={0} sx={{ p: 0, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="h6">{t('frequency.summary')}</Typography>
          </Box>
          <Divider />
          <List sx={{ width: '100%', bgcolor: 'background.paper', p: 0 }}>
            {selectedPosition.users.map((user, index) => (
              <React.Fragment key={user.id}>
                {index > 0 && <Divider variant="inset" component="li" />}
                <ListItem alignItems="center" sx={{ py: 2 }}>
                  <ListItemAvatar>
                    <Avatar src={user.profilePicture} alt={user.name}>
                      {user.name.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={user.name}
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={(user.count / maxCount) * 100}
                          sx={{
                            height: 8,
                            borderRadius: 5,
                            width: '100%',
                            mr: 2,
                            backgroundColor: theme.palette.grey[200],
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: theme.palette.primary.main,
                            }
                          }}
                        />
                      </Box>
                    }
                  />
                  <Typography variant="body1" fontWeight="bold" sx={{ ml: 2 }}>
                    {user.count}x
                  </Typography>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      </Box>
    );
  };

  // Render position cards grid view
  const renderPositionsGrid = () => {
    return (
      <Grid container spacing={3}>
        {positions.map((position) => (
          <Grid item xs={12} sm={6} md={4} key={position.name}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                },
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
              onClick={() => handleSelectPosition(position)}
            >
              <CardHeader
                title={position.name}
                subheader={position.date || 'All time'}
                sx={{
                  bgcolor: theme.palette.primary.light,
                  color: theme.palette.primary.contrastText,
                  '& .MuiCardHeader-subheader': {
                    color: theme.palette.primary.contrastText
                  }
                }}
              />
              <CardContent sx={{ flexGrow: 1, p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  {t('frequency.summary')}
                </Typography>
                <Stack spacing={2}>
                  {position.users.slice(0, 4).map((user) => {
                    const maxCount = getMaxCount(position.users);
                    return (
                      <Box key={user.id} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                          src={user.profilePicture}
                          alt={user.name}
                          sx={{ width: 32, height: 32, mr: 1.5 }}
                        >
                          {user.name.charAt(0)}
                        </Avatar>
                        <Box sx={{ flexGrow: 1, mr: 1 }}>
                          <Typography variant="body2" noWrap>
                            {user.name}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={(user.count / maxCount) * 100}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              mt: 0.5,
                              backgroundColor: theme.palette.grey[200],
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: theme.palette.primary.main,
                              }
                            }}
                          />
                        </Box>
                        <Typography variant="body2" fontWeight="bold">
                          {user.count}x
                        </Typography>
                      </Box>
                    );
                  })}
                  {position.users.length > 4 && (
                    <Typography variant="body2" color="text.secondary" align="center">
                      +{position.users.length - 4} more...
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box sx={{ py: 3 }}>
      <Container maxWidth="lg">
        {!selectedPosition && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <PageHeader 
              title={t('frequency.title')} 
              subtitle={t('frequency.subtitle')} 
            />
            <Button
              variant="outlined"
              color="primary"
              startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing || loading}
            >
              {t('common.refresh')}
            </Button>
          </Box>
        )}

        {useMockData && !loading && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Using demo data. Connect to the API to see real frequency data.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Grid container spacing={3}>
            {renderSkeletons()}
          </Grid>
        ) : positions.length === 0 ? (
          <Typography align="center" sx={{ my: 4 }}>
            {t('frequency.noData')}
          </Typography>
        ) : selectedPosition ? (
          renderPositionDetail()
        ) : (
          renderPositionsGrid()
        )}
      </Container>
    </Box>
  );
};

export default Frequency;
